import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  api,
  Group,
  GroupMember,
  GroupPermission,
  PermissionLevel,
  OrgProject,
  User,
} from '../../api';
import { getErrorMessage } from '../../../../shared/src';
import { PermissionSelector } from '../ui/PermissionSelector';
import { CreateGroupModal } from '../modals';
import { ExpandableResourceCell, ExpandableCellItem } from './ExpandableResourceCell';

type Column = 'users' | 'projects';

interface ExpandedCell {
  groupId: string;
  column: Column;
}

interface GroupCache {
  members?: GroupMember[];
  permissions?: GroupPermission[];
}

interface PermissionsGroupsSubTabProps {
  orgId: string;
}

export function PermissionsGroupsSubTab({ orgId }: PermissionsGroupsSubTabProps) {
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const [expandedCell, setExpandedCell] = useState<ExpandedCell | null>(null);
  const [groupCache, setGroupCache] = useState<Record<string, GroupCache>>({});
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const loadData = async () => {
    try {
      // Single round-trip: the server pre-joins each group's members and
      // permissions, replacing the old 1 + 2N request fan-out.
      const overview = await api.getGroupsPermissionsOverview(orgId);
      setGroups(overview.groups);
      setUsers(overview.users);
      setProjects(overview.projects);
      setGroupCache(
        Object.fromEntries(
          overview.groups.map((g) => [
            g.id,
            {
              members: overview.groupMembers[g.id] ?? [],
              permissions: overview.groupPermissions[g.id] ?? [],
            },
          ])
        )
      );
      setError('');
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCell = (groupId: string, column: Column) => {
    if (expandedCell && expandedCell.groupId === groupId && expandedCell.column === column) {
      setExpandedCell(null);
      return;
    }
    setExpandedCell({ groupId, column });
  };

  const handleCreateSuccess = (group: Group) => {
    setGroups([...groups, group]);
    setShowCreateModal(false);
    setActionMessage({ type: 'success', text: `Group "${group.name}" created successfully` });
  };

  const handleToggleMember = async (groupId: string, userId: string, isCurrentlyMember: boolean) => {
    setUpdatingKey(`member-${userId}`);
    try {
      // Per-member add/remove (not a replace-all PUT): independent, idempotent
      // ops so toggling several members in quick succession can't clobber each
      // other. The endpoint returns the authoritative membership snapshot.
      const updated = isCurrentlyMember
        ? await api.removeOrgGroupMember(orgId, groupId, userId)
        : await api.addOrgGroupMember(orgId, groupId, userId);
      setGroupCache((prev) => ({ ...prev, [groupId]: { ...prev[groupId], members: updated } }));
      setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, member_count: updated.length } : g)));
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update members: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingKey(null);
    }
  };

  const handlePermissionChange = async (
    groupId: string,
    projectId: string,
    permission: PermissionLevel | null
  ) => {
    setUpdatingKey(`project-${projectId}`);
    try {
      await api.setOrgGroupPermission(orgId, groupId, projectId, permission);
      const refreshed = await api.getOrgGroupPermissions(orgId, groupId);
      setGroupCache((prev) => ({ ...prev, [groupId]: { ...prev[groupId], permissions: refreshed } }));
      setActionMessage({ type: 'success', text: 'Permission updated successfully' });
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to update permission: ${getErrorMessage(err)}` });
    } finally {
      setUpdatingKey(null);
    }
  };

  const handleStartRename = (group: Group) => {
    setRenamingGroupId(group.id);
    setRenameValue(group.name);
  };

  const handleCancelRename = () => {
    setRenamingGroupId(null);
    setRenameValue('');
  };

  const handleSaveRename = async (groupId: string) => {
    if (!renameValue.trim()) return;
    setActionInProgress(`rename-${groupId}`);
    try {
      const updated = await api.updateOrgGroup(orgId, groupId, renameValue.trim());
      setGroups(groups.map((g) => (g.id === groupId ? { ...g, ...updated } : g)));
      setRenamingGroupId(null);
      setRenameValue('');
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to rename group: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    if (!window.confirm(`Delete group "${groupName}"? This action cannot be undone.`)) return;
    setActionInProgress(`delete-${groupId}`);
    try {
      await api.deleteOrgGroup(orgId, groupId);
      setGroups(groups.filter((g) => g.id !== groupId));
      if (expandedCell?.groupId === groupId) setExpandedCell(null);
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to delete group: ${getErrorMessage(err)}` });
    } finally {
      setActionInProgress(null);
    }
  };

  const userItems = (groupId: string): ExpandableCellItem[] => {
    const members = groupCache[groupId]?.members ?? [];
    return members.map((m) => ({ key: m.user_id, label: `${m.first_name} ${m.last_name}`.trim() || m.email }));
  };

  const projectItems = (groupId: string): ExpandableCellItem[] => {
    const perms = groupCache[groupId]?.permissions ?? [];
    return perms.map((p) => ({ key: p.project_id, label: p.project_name, level: p.permission }));
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">Loading groups...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-message">{error}</div>
        <button className="btn-primary" onClick={loadData}>Retry</button>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <div className="tab-header">
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          Create Group
        </button>
      </div>

      {actionMessage && (
        <div className={actionMessage.type === 'success' ? 'success-message' : 'error-message'}>
          {actionMessage.text}
        </div>
      )}

      <table className="data-table">
        <thead>
          <tr>
            <th>Group</th>
            <th>Users</th>
            <th>Projects</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const usersExpanded = expandedCell?.groupId === group.id && expandedCell.column === 'users';
            const projectsExpanded = expandedCell?.groupId === group.id && expandedCell.column === 'projects';
            const anyExpanded = usersExpanded || projectsExpanded;
            const isRenaming = renamingGroupId === group.id;
            const isProcessing = actionInProgress?.includes(group.id);
            return (
              <Fragment key={group.id}>
                <tr>
                  <td>
                    {isRenaming ? (
                      <div className="inline-form">
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename(group.id);
                            if (e.key === 'Escape') handleCancelRename();
                          }}
                          autoFocus
                        />
                        <button
                          className="btn-small btn-primary"
                          onClick={() => handleSaveRename(group.id)}
                          disabled={!renameValue.trim() || actionInProgress === `rename-${group.id}`}
                        >
                          Save
                        </button>
                        <button className="btn-small btn-secondary" onClick={handleCancelRename}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className="expandable-row-toggle as-static">{group.name}</span>
                    )}
                  </td>
                  <td>
                    <ExpandableResourceCell
                      items={userItems(group.id)}
                      pluralLabel="users"
                      singularLabel="user"
                      expanded={usersExpanded}
                      onToggle={() => handleToggleCell(group.id, 'users')}
                    />
                  </td>
                  <td>
                    <ExpandableResourceCell
                      items={projectItems(group.id)}
                      pluralLabel="projects"
                      singularLabel="project"
                      expanded={projectsExpanded}
                      onToggle={() => handleToggleCell(group.id, 'projects')}
                    />
                  </td>
                  <td className="actions-cell">
                    {isProcessing ? (
                      <span className="processing">Processing...</span>
                    ) : (
                      <div className="action-buttons">
                        <button className="btn-small btn-secondary" onClick={() => handleStartRename(group)}>
                          Rename
                        </button>
                        <button className="btn-small btn-danger" onClick={() => handleDeleteGroup(group.id, group.name)}>
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {anyExpanded && (
                  <tr className="expansion-row">
                    <td colSpan={4}>
                      {usersExpanded ? (
                        <MembersPanel
                          users={users}
                          memberIds={new Set((groupCache[group.id]?.members ?? []).map((m) => m.user_id))}
                          onToggle={(userId, isMember) => handleToggleMember(group.id, userId, isMember)}
                          updatingKey={updatingKey}
                        />
                      ) : (
                        <ProjectPermissionsPanel
                          projects={projects}
                          permissionFor={(projectId) =>
                            groupCache[group.id]?.permissions?.find((p) => p.project_id === projectId)?.permission ?? null
                          }
                          onChange={(projectId, permission) => handlePermissionChange(group.id, projectId, permission)}
                          updatingKey={updatingKey}
                        />
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {groups.length === 0 && (
            <tr>
              <td colSpan={4} className="empty-state">
                No groups yet. Click "Create Group" to add the first group.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {showCreateModal && (
        <CreateGroupModal
          orgId={orgId}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </div>
  );
}

function MembersPanel({
  users,
  memberIds,
  onToggle,
  updatingKey,
}: {
  users: User[];
  memberIds: Set<string>;
  onToggle: (userId: string, isMember: boolean) => void;
  updatingKey: string | null;
}) {
  const sorted = useMemo(
    () => [...users].sort((a, b) => {
      const aLabel = `${a.first_name} ${a.last_name}`.trim() || a.email;
      const bLabel = `${b.first_name} ${b.last_name}`.trim() || b.email;
      return aLabel.localeCompare(bLabel);
    }),
    [users]
  );
  if (sorted.length === 0) {
    return <div className="empty-state">No users in this organization.</div>;
  }
  return (
    <div className="permission-editor-section">
      <h5>Members</h5>
      <div className="checkbox-list">
        {sorted.map((user) => {
          const isMember = memberIds.has(user.id);
          const isUpdating = updatingKey === `member-${user.id}`;
          return (
            <label key={user.id} className={`checkbox-item ${isUpdating ? 'disabled' : ''}`}>
              <input
                type="checkbox"
                checked={isMember}
                disabled={isUpdating}
                onChange={() => onToggle(user.id, isMember)}
              />
              <span className="checkbox-label">
                {user.first_name} {user.last_name}
                <span className="checkbox-detail">{user.email}</span>
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ProjectPermissionsPanel({
  projects,
  permissionFor,
  onChange,
  updatingKey,
}: {
  projects: OrgProject[];
  permissionFor: (projectId: string) => PermissionLevel | null;
  onChange: (projectId: string, permission: PermissionLevel | null) => void;
  updatingKey: string | null;
}) {
  const sorted = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );
  if (sorted.length === 0) {
    return <div className="empty-state">No projects available.</div>;
  }
  return (
    <div className="permission-editor-section">
      <h5>Project Permissions</h5>
      <table className="data-table">
        <thead>
          <tr>
            <th>Project</th>
            <th>Permission</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((project) => (
            <tr key={project.id}>
              <td>{project.name}</td>
              <td>
                <PermissionSelector
                  currentPermission={permissionFor(project.id)}
                  isUpdating={updatingKey === `project-${project.id}`}
                  onPermissionChange={(perm) => onChange(project.id, perm)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
